import React, { useEffect } from 'react'
import Spinner from './Spinner'
import BtnIcon from './BtnIcon'
import { ReactSVG } from 'react-svg'
import Upload from './Upload'
import Btn from './Btn'
import { toast } from 'react-toastify'
import { useAxios } from '../hooks/useAxios'
import { observer } from 'mobx-react-lite'
import { validateFile } from '../utils/validateFile'

export type UploadedFile = {
  name: string
  size: number
  type: string
  href: string
}

interface IProps {
  className?: string
  file?: File | UploadedFile | null
  onChangeFile: (newFile: File | null) => void
  resetFile?: boolean
  text?: string
  note?: string
  errorText?: string
  disabled?: boolean
  uploadedFile?: UploadedFile | null
  onRemoveUploaded?: () => void
}

const IMAGE_FORMATS = ['jpeg', 'png', 'jpg']

const getExtFromName = (name: string) => name.split('.').pop() ?? ''
const getTypeLabel = (t?: string) => {
  if (!t) return 'FILE'
  if (t.includes('/')) return (t.split('/')[1] || 'FILE').toUpperCase()
  return t.toUpperCase()
}

const ImageUpload = observer((props: IProps,) => {
const [file, setFile] = React.useState<File | UploadedFile | null>(null)
  const [preview, setPreview] = React.useState<string | null>(null)
  const [fileExt, setFileExt] = React.useState<string>('')
  const { loading } = useAxios()

  useEffect(() => {
    if (!props.file) {
      setFile(null)
      setPreview(null)
      setFileExt('')
      return
    }

    const ext = getExtFromName(props.file.name)
    setFile(props.file)
    setFileExt(ext)
  }, [props.file])

  useEffect(() => {
    if (!file) {
      setPreview(null)
      return
    }

    let objectURL: string | null = null

    const handler = async () => {
      if (file instanceof File) {
        const objectURL = URL.createObjectURL(file)
        setPreview(objectURL)

        return
      }

      const res = await fetch((file as UploadedFile).href)
      const blob = await res.blob()
      objectURL = URL.createObjectURL(blob)
      setPreview(objectURL)
    }

    handler()

    return () => {
      typeof objectURL === 'string' && URL.revokeObjectURL(objectURL)
    }
  }, [file])

  useEffect(() => {
    if (props.resetFile) {
      setFile(null)
      setPreview(null)
      setFileExt('')
    }
  }, [props.resetFile])

  const onFileChange = async (file: File) => {
    const res = validateFile(file)

    if (res) {
      toast.error(res)
    } else {
      const ext = getExtFromName(file.name)
      props.onChangeFile(file)
      setFile(file)
      setFileExt(ext)
    }
  }

  const hasLocal = !!preview
  const hasUploaded = !hasLocal && !!props.uploadedFile

  const shownName = hasLocal ? props.file?.name : props.uploadedFile?.name
  const shownSize = hasLocal ? props.file?.size : props.uploadedFile?.size
  const shownType = hasLocal
    ? props.file?.type?.split('/')[1]?.toUpperCase()
    : getTypeLabel(props.uploadedFile?.type)

  const shownExt = hasLocal
    ? fileExt
    : props.uploadedFile
      ? getExtFromName(props.uploadedFile.name)
      : ''

  const removeCurrent = async () => {
    if (hasLocal) {
      setFile(null)
      setPreview(null)
      setFileExt('')
      props.onChangeFile(null)
      return
    }

    if (hasUploaded) {
      props.onChangeFile(null)
      props.onRemoveUploaded?.()
    }
  }

  return (
    <div className={'image-upload'} >
      <div className={'image-upload__wrapper'}>
        <p className={'image-upload__title'}>Выберите файл с устройства</p>
        {props.text && <p className={'image-upload__text'}>{props.text}</p>}
        <div
          className={`image-upload__container ${props.className ?? ''}`.trim()}
        >
          {loading && (
            <div className={'avatar-upload__progress'}>
              <Spinner></Spinner>
            </div>
          )}

          <div className="card-content u-flex" style={{ width: '100%' }}>
            {!hasLocal && !hasUploaded ? (
              <Upload
                onChange={async (file: File) => await onFileChange(file)}
                activator={(handler) => (
                  <Btn disabled={props.disabled} onClick={handler}>Выбрать файл</Btn>
                )}
              ></Upload>
            ) : (
              <div className={'image-upload__content'}>
                <div className={'image-upload__container'}>
                  <div className={'image-upload__image'}>
                    {hasLocal &&
                    preview &&
                    IMAGE_FORMATS.includes(fileExt.toLowerCase()) ? (
                      <img src={preview} alt="image" />
                    ) : (
                      <img
                        src={`/i/${(shownExt || 'FILE').toUpperCase()}.svg`}
                        alt="image"
                        className="image-upload__icon"
                      />
                    )}
                  </div>
                </div>
                <div
                  className={'image-upload__fileinfo'}
                  style={{ flexBasis: '100%' }}
                >
                  <p>{shownName}</p>
                  <div className="u-flex ">
                    <p className={'image-upload__filesize'}>
                      {shownType}&nbsp;
                    </p>
                    <p className={'image-upload__filesize'}>
                      {shownSize
                        ? `${Math.round(shownSize / 1024)} KB`
                        : 'Нет данных'}
                    </p>
                  </div>
                </div>
                <div className={'u-flex'}>
                  <BtnIcon
                    disabled={props.disabled}
                    className={'card-head__control _right-side'}
                    onClick={removeCurrent}
                    aria-label="Удалить файл"
                    type="button"
                  >
                    <ReactSVG src={'/i/svg/x.svg'} wrapper={'span'}></ReactSVG>
                  </BtnIcon>
                </div>
              </div>
            )}
          </div>
        </div>

        {hasUploaded && props.uploadedFile?.href && (
          <div className={'mt-md-8 mt-sm-6'}>
            <Btn disabled={props.disabled} href={props.uploadedFile.href} target={'_blank'}>
              Открыть файл
            </Btn>
          </div>
        )}
      </div>
      {props.note && <p className={'image-upload__text'}>{props.note}</p>}
      {props.errorText && <p className={'error-text'}>{props.errorText}</p>}
    </div>
  )
})

export default ImageUpload
